// Tiny presentational pieces shared across the heatmap: a hue-tinted avatar, a
// tenant tag pill, and the 7-day "week strip". Colors come from `hashHue` so
// each doctor/tenant is stable and distinct. These intentionally do NOT reuse
// the shadcn `Avatar` (that one is a Radix image/fallback, not a hue circle).

import { cn } from "@/lib/utils";
import { useHideSunday } from "@/hooks/use-user-preferences";
import { getVisibleWeekdayColumns, hashHue } from "./heatmap-utils";

function initialsOf(name) {
  return (
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() || "")
      .join("") || "?"
  );
}

// Initials circle tinted by the entity id's stable hue.
export function HueAvatar({ name, id, size = 24, className }) {
  const hue = hashHue(id ?? name);
  return (
    <span
      className={cn(
        "inline-grid shrink-0 place-items-center rounded-full font-semibold text-white",
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, Math.round(size * 0.38)),
        background: `oklch(0.55 0.14 ${hue})`,
      }}
    >
      {initialsOf(name)}
    </span>
  );
}

// Uppercase pill for a tenant/location, tinted by the entity id's hue. `--tag-l`
// flips the text lightness with the theme — a dark tone on the pale tint in
// light mode, a light tone in dark mode — so the label stays legible in both.
export function Tag({ id, children, className }) {
  const hue = hashHue(id ?? children);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-[4px] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        "[--tag-l:0.45] dark:[--tag-l:0.86]",
        className,
      )}
      style={{
        color: `oklch(var(--tag-l) 0.12 ${hue})`,
        background: `oklch(0.62 0.16 ${hue} / 0.16)`,
        boxShadow: `inset 0 0 0 1px oklch(0.62 0.16 ${hue} / 0.3)`,
      }}
    >
      {children}
    </span>
  );
}

// Horizontal row of 7 mini cells, ordered by the configured first day of week.
// `active` is a list of ISO weekday numbers (1=Mon). Active cells take the
// entity hue when `id` is given.
export function WeekStrip({ active = [], size = "sm", id }) {
  const columns = getVisibleWeekdayColumns(useHideSunday());
  const cell =
    size === "lg"
      ? "h-5 w-5 text-[10px]"
      : size === "md"
        ? "h-4 w-4 text-[9px]"
        : "h-3.5 w-3.5 text-[8px]";
  const hue = id != null ? hashHue(id) : null;
  return (
    <div className="inline-flex items-center gap-[3px]">
      {columns.map((col, idx) => {
        const on = active.includes(col.iso);
        const isWeekend = col.weekend;
        return (
          <span
            key={idx}
            className={cn(
              "grid select-none place-items-center rounded-[3px] font-semibold tabular-nums",
              cell,
              on
                ? "bg-primary text-primary-foreground"
                : isWeekend
                  ? "bg-muted text-muted-foreground/60"
                  : "bg-muted text-muted-foreground",
            )}
            style={
              on && hue != null
                ? { background: `oklch(0.62 0.16 ${hue})`, color: "#fff" }
                : undefined
            }
          >
            {col.letter}
          </span>
        );
      })}
    </div>
  );
}
